import React from 'react';
import type { Message, ConversationStatus } from '../types';
import { MESSAGE_TYPE_LABEL, STATUS_LABEL } from '../types';
import { findSenderParticipant, useMessagesContext } from '../MessagesContext';
import { formatTime } from '../../../utils/datetime';

/** system_notice 의 status 전이 payload 를 한글 본문으로 렌더 (없으면 null). */
function renderSystemTransition(m: Message): React.ReactNode {
 if (m.message_type !== 'system_notice') return null;
 const p = m.payload as { from_status?: string; to_status?: string };
 if (!p?.from_status || !p?.to_status) return null;
 const fromLabel = STATUS_LABEL[p.from_status as ConversationStatus] ?? p.from_status;
 const toLabel = STATUS_LABEL[p.to_status as ConversationStatus] ?? p.to_status;
 return (
  <div className="text-sm text-gray-700">
   <span className="text-gray-500">상태:</span>{' '}
   <span className="font-medium">{fromLabel}</span>
   <span className="mx-2 text-gray-400">→</span>
   <span className="font-medium">{toLabel}</span>
  </div>
 );
}

const TYPE_TONE: Partial<Record<string, string>> = {
 text: 'bg-gray-100 text-gray-700',
 system_notice: 'bg-yellow-50 text-yellow-700',
 proposal_offer: 'bg-blue-100 text-blue-700',
 proposal_counter: 'bg-orange-100 text-orange-700',
 proposal_accept: 'bg-green-100 text-green-700',
 proposal_reject: 'bg-red-100 text-red-700',
 proposal_cancel: 'bg-gray-200 text-gray-600',
 invitation_offer: 'bg-purple-100 text-purple-700',
 invitation_counter: 'bg-orange-100 text-orange-700',
 invitation_accept: 'bg-green-100 text-green-700',
 invitation_reject: 'bg-red-100 text-red-700',
};

interface Props {
 message: Message;
 viewerPeerHash?: string;  // backward compat — 있으면 우선, 없으면 Context 의 viewerPeerHash 사용
 children?: React.ReactNode;
}

/**
 * 타임라인 dot + 시각 + 메시지 chrome.
 *
 * - 좌측: dot (message_type 별 색) + 시각 (HH:mm).
 * - 헤더: 한글 type 배지 + 친화 sender (클릭 시 PeerInfoModal).
 * - 본문: children (어댑터가 message_type 별 렌더링) 또는 fallback body.
 */
const MessageItem: React.FC<Props> = ({ message: m, viewerPeerHash, children }) => {
 const { participants, viewerPeerHash: ctxViewer, onPeerClick } = useMessagesContext();
 const viewer = viewerPeerHash ?? ctxViewer;

 const sender = findSenderParticipant(participants, m.sender_peer_hash);
 const isMine = !!m.sender_peer_hash && m.sender_peer_hash === viewer;
 const senderLabel = m.sender_peer_hash === null
  ? '시스템'
  : isMine
  ? '나'
  : (sender?.peer_name || sender?.family_name || sender?.peer_email || '알 수 없음');
 const senderClickable = !isMine && m.sender_peer_hash !== null && !!sender;

 const timeStr = formatTime(m.created_at);

 return (
  <li className="relative py-3 pl-2">
   {/* 메시지 종류 태그 — 레일 '위'에 중앙정렬로 얹음(과거 dot 대체). 상태 변경/역제안/가입 제안 등. */}
   <span
    className={`absolute top-3 left-[-32px] -translate-x-1/2 whitespace-nowrap text-[10px] px-1.5 py-0.5 rounded border border-white ${TYPE_TONE[m.message_type] ?? 'bg-gray-100 text-gray-600'}`}
   >
    {MESSAGE_TYPE_LABEL[m.message_type] ?? m.message_type}
   </span>
   <div className="flex items-center gap-2 mb-1 flex-wrap">
    <span className="text-xs text-gray-400">{timeStr}</span>
    {m.sender_peer_hash === null ? (
     <span className="text-xs text-yellow-700">시스템</span>
    ) : senderClickable ? (
     <button
      onClick={() => onPeerClick(sender!)}
      className="text-xs text-gray-600 hover:text-blue-600 hover:underline"
      title={m.sender_peer_hash}
     >{senderLabel}</button>
    ) : (
     <span className={`text-xs ${isMine ? 'text-blue-600 font-medium' : 'text-gray-500'}`}>
      {senderLabel}
     </span>
    )}
   </div>
   {renderSystemTransition(m)
    ?? children
    ?? (m.body ? <div className="text-sm whitespace-pre-wrap">{m.body}</div> : null)}
  </li>
 );
};

export default MessageItem;
